// ===== WorkBadge: 방 조용한 배지 + 방 안 팝오버 (Phase 3, T7 → records-ssot 4단계) =====
// 업무 감지 결과는 방 안 조용한 배지(미처리 후보 수)로만 노출하고, 클릭하면 이동 없는 방 안 팝오버에 그 방의
// 미처리 후보를 **채팅 인라인 카드와 같은 카드**(WorkInlineCards.buildCard)로 보여 준다 — 제목·담당을 그 자리에서
// 고치고 [등록] 한 번, 또는 [무시](결정 F5·F20). 검토 문서 생성·후보 편집 폼은 없다.
// 부재 멤버 커버는 서버 진실(GET /api/work/outbox) 기반으로 복원한다 — 실시간 이벤트(work-detected/
// work-outbox-resolved)는 viewingSessionId 게이트로 방을 보는 멤버에게만 도달하므로 게이트를 우회하지 않는다.
//
// #chatHeader는 textContent 계약(app.js renderChatHeader)이라 손대지 않는다 — 배지는 index.html의
// 별도 엘리먼트(#workBadge)에 렌더한다.

function isWorkBadgeRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }

// 방 배지가 세는 레코드: 이 방(chatId)의 미처리(pending_review) 후보. 카드 렌더용 원본은 그대로 들고 있는다.
function workBadgeRecordInfo(value: unknown): { id: string; chatId?: string; state?: string } | null {
  if (!isWorkBadgeRecord(value) || !isWorkBadgeRecord(value.item)) return null;
  const item = value.item;
  if (typeof item.id !== "string") return null;
  const contextWindow = isWorkBadgeRecord(item.contextWindow) ? item.contextWindow : null;
  return {
    id: item.id,
    chatId: contextWindow && typeof contextWindow.chatId === "string" ? contextWindow.chatId : undefined,
    state: typeof item.state === "string" ? item.state : undefined,
  };
}

var WorkBadge = (function () {
  var _sessionId: string | null = null;
  var _items: unknown[] = [];
  var _open = false;

  async function _fetchJson(url: string): Promise<Record<string, unknown>> {
    var response = await fetch(url);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!isWorkBadgeRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      throw new Error(typeof payload.error === "string" ? payload.error : "Request failed: " + response.status);
    }
    return payload;
  }

  function _badgeEl() {
    return document.getElementById("workBadge");
  }

  function _popoverEl() {
    return document.getElementById("workBadgePopover");
  }

  function _renderBadge() {
    var el = _badgeEl();
    if (!el) return;
    var count = _items.length;
    if (count === 0) {
      el.style.display = "none";
      el.textContent = "";
      el.removeAttribute("aria-label");
      if (_open) _closePopover();
      return;
    }
    el.textContent = String(count);
    el.setAttribute("aria-label", "업무 후보 " + count + "건");
    el.style.display = "";
  }

  // ===== 서버 진실 기반 복원 =====

  async function refresh(sessionId?: string | null): Promise<void> {
    if (sessionId !== undefined) _sessionId = sessionId;
    if (!_sessionId) {
      _items = [];
      _renderBadge();
      return;
    }
    var chatId = "chat:" + _sessionId;
    try {
      var data = await _fetchJson("/api/work/outbox");
      var items = Array.isArray(data.items) ? data.items : [];
      _items = items.filter(function (record: unknown) {
        var info = workBadgeRecordInfo(record);
        return !!info && info.chatId === chatId && info.state === "pending_review"; // 전체 가시: visibility 무관 노출
      });
    } catch (error) {
      console.error("[work-badge] refresh 실패:", error instanceof Error ? error.message : String(error));
      _items = [];
    }
    _renderBadge();
    if (_open) _renderPopoverList();
  }

  // ===== 팝오버 =====

  function _onOutsideClick(event: MouseEvent): void {
    var pop = _popoverEl();
    var badge = _badgeEl();
    if (pop && event.target instanceof Node && pop.contains(event.target)) return;
    if (badge && event.target instanceof Node && badge.contains(event.target)) return;
    _closePopover();
  }

  function _onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") _closePopover();
  }

  function _closePopover() {
    var pop = _popoverEl();
    if (pop) pop.remove();
    _open = false;
    document.removeEventListener("click", _onOutsideClick, true);
    document.removeEventListener("keydown", _onKeydown);
  }

  function _renderPopoverList() {
    var pop = _popoverEl();
    if (!pop) return;
    pop.textContent = "";
    if (!_items.length) {
      var empty = document.createElement("div");
      empty.className = "work-badge-popover-empty";
      empty.textContent = "처리할 업무 후보가 없어요.";
      pop.appendChild(empty);
      return;
    }
    if (typeof WorkInlineCards === "undefined") return;
    var host = pop;
    _items.forEach(function (record) {
      host.appendChild(WorkInlineCards.buildCard(record, {
        context: "badge",
        // 처리한 카드는 팝오버 안에서 접힌 한 줄로 남기고, 배지 숫자만 서버 진실로 다시 센다.
        onResolved: function () { void _recount(); },
      }));
    });
  }

  // 팝오버를 다시 그리지 않고 배지 숫자만 갱신한다(방금 처리한 카드의 결과 줄을 사용자가 볼 수 있게).
  async function _recount(): Promise<void> {
    var open = _open;
    _open = false;
    await refresh(_sessionId);
    _open = open && !!_popoverEl();
  }

  function openPopover() {
    if (_open) {
      _closePopover();
      return;
    }
    var badge = _badgeEl();
    if (!badge) return;

    var pop = document.createElement("div");
    pop.id = "workBadgePopover";
    pop.className = "work-badge-popover";
    pop.setAttribute("role", "dialog");
    pop.setAttribute("aria-label", "이 방의 업무 후보");
    document.body.appendChild(pop);

    var rect = badge.getBoundingClientRect();
    var popWidth = pop.offsetWidth || 380;
    pop.style.left = Math.max(8, Math.min(rect.left, window.innerWidth - popWidth - 8)) + "px";
    pop.style.top = (rect.bottom + 6) + "px";

    _open = true;
    _renderPopoverList();

    setTimeout(function () {
      document.addEventListener("click", _onOutsideClick, true);
      document.addEventListener("keydown", _onKeydown);
    }, 0);
  }

  // ===== ws 이벤트 위임 대상 (ws-handlers.js가 호출) =====

  // 다른 방을 보는 중이면 무렌더 (onRoomMessage 관례). resolved 이벤트는 actor 자신도 수신하므로
  // work-detected와 동일하게 멱등 재조회로 카운트/팝오버를 동기화한다.
  function _onWorkEvent(data: unknown): void {
    if (!isWorkBadgeRecord(data) || typeof data.sessionId !== "string" || data.sessionId !== window.currentSessionId) return;
    refresh(window.currentSessionId);
  }

  function onWorkDetected(data: unknown): void {
    _onWorkEvent(data);
  }

  function onWorkOutboxResolved(data: unknown): void {
    _onWorkEvent(data);
  }

  return {
    refresh: refresh,
    openPopover: openPopover,
    onWorkDetected: onWorkDetected,
    onWorkOutboxResolved: onWorkOutboxResolved,
  };
})();

document.addEventListener("DOMContentLoaded", function () {
  var badge = document.getElementById("workBadge");
  if (badge) badge.addEventListener("click", function () { WorkBadge.openPopover(); });
});
